// The lot writes offline (BL-2431, ADR 066 — Offline Mode § 3 and § 4): a Save and both create doors
// become change-queue entries (through `buildQueuedEntry` only) and update the device's copy, so the
// offline screens show the change. Each answer is the online route's own DTO. BL-2447 adds a Save's
// and a create's photos and documents, one entry per item, and BL-2418 a Save's photo order and Main.
// A move and a relist are BL-2448's (`offline-placement-handlers.ts`, which routes them before these).
import { formatLotNumber } from '@auctogon/core/isomorphic/lot-number';
import type { LotCreateRequestPayload } from '@auctogon/core-admin/schemas/item-form';
import { LOT_STATE_WRITE_FIELD_KEYS } from '@auctogon/core-admin/schemas/lot-state-write';
import type { OfflineReferenceDto } from '@auctogon/core-admin/schemas/responses/offline-reference';
import type { InternalNoteActorDto } from '@auctogon/core-admin/schemas/responses/internal-note';
import {
  isRecord,
  type ChangeQueueJson,
  type ChangeQueueLabel,
  type JsonObject,
} from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import {
  applyLotAttachmentOverlay,
  attachmentQueueEntries,
  splitOfflineCreateFiles,
  type OfflineAttachmentChanges,
  type OfflineDocumentAdd,
  type OfflineMediaAdd,
} from '@auctogon/admin-isomorphic/isomorphic/offline/offline-attachments';
import { applySave, type LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import { diffLotPatch, type LotPatchFields } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-lot-edit';
import { applyOfflineMediaOrder, mediaOrderQueueEntry } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-media-order';
import { applyLotEditOverlay } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-lot-overlay';
import { buildOfflineNewLot, type OfflineNewLotPlacement } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-new-lot';
import { createSheetNotes } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-notes';

import { lotPatchBaseline } from '../../components/lots/lot-edit-payloads';
import { OFFLINE_SESSION_RECORD_KEY, type OfflineStore } from '../../gateways/native-device';
import { OFFLINE_REFERENCE_KEY } from '../../stores/offline-sync/offline-sync-engine';
import type { OfflineRequest } from './create-offline-transport';
import { NOT_FOUND } from './offline-answers';
import { cacheStagedListThumbnails } from './offline-thumbnails';
import { commitLocalWrite, pendingCreateOf, unqueueable, withQueue, type WriteContext } from './offline-write-support';

/** The fields whose picked id the device copy names from the synced reference. */
const NAMED_PICKS = ['sellerId', 'salesRepId', 'taxRateId', 'locationId'];

const newId = () => globalThis.crypto.randomUUID();

/**
 * The synced picker lists, read only when a change picks one of their records: a title or price
 * Save never decodes every seller on the device.
 */
async function referenceFor(store: OfflineStore, fields: LotPatchFields | LotCreateRequestPayload): Promise<OfflineReferenceDto> {
  if (!NAMED_PICKS.some((field) => field in fields)) return {};
  return (await store.records.get({ collection: 'reference', key: OFFLINE_REFERENCE_KEY }))?.reference ?? {};
}

/**
 * @param store - the scope's store.
 * @returns the signed-in user as a note's author, from the stored session, or null.
 */
export async function sessionAuthor(store: OfflineStore): Promise<InternalNoteActorDto | null> {
  const session = await store.records.get({ collection: 'session', key: OFFLINE_SESSION_RECORD_KEY });
  return session ? { staffUserId: session.user.id, name: session.user.name ?? null } : null;
}

/** A create body's lot fields, when it names a lot. */
function newLotOf(value: unknown): LotCreateRequestPayload | null {
  if (!isRecord(value) || typeof value.name !== 'string') return null;
  return value as unknown as LotCreateRequestPayload;
}

const stringsOf = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((v): v is string => typeof v === 'string') ? value : null;

const recordsOf = (value: unknown): JsonObject[] | null =>
  Array.isArray(value) && value.every(isRecord) ? value : null;

/** A Save's photo and document changes, and its photo order when the Save carries one. */
interface SaveAttachmentChanges {
  attachments: OfflineAttachmentChanges;
  /** The photo ids in their new order, the first as Main (BL-2418); null when the Save sent none. */
  order: string[] | null;
}

/**
 * A Save's photo and document change lists (BL-2348's shape), with the photo order a reorder or
 * "Set as main" adds (BL-2418), or null when a list is not the shape.
 */
function attachmentChangesOf(body: JsonObject): SaveAttachmentChanges | null {
  const empty = { add: [], remove: [] };
  const lists = (value: unknown) => {
    if (value === undefined) return empty;
    if (!isRecord(value)) return null;
    const add = recordsOf(value.add);
    const remove = stringsOf(value.remove);
    return add && remove ? { add, remove } : null;
  };
  const media = lists(body.mediaChanges);
  const documents = lists(body.documentChanges);
  if (!media || !documents) return null;
  const rawOrder = isRecord(body.mediaChanges) ? body.mediaChanges.order : undefined;
  const order = rawOrder === undefined ? null : stringsOf(rawOrder);
  if (rawOrder !== undefined && (!order || order.length === 0)) return null;
  // The drawer's in-memory records, as the form built them: the server checks every field at replay.
  return {
    attachments: {
      media: { add: media.add as unknown as OfflineMediaAdd[], remove: media.remove },
      documents: { add: documents.add as unknown as OfflineDocumentAdd[], remove: documents.remove },
    },
    order,
  };
}

const hasAttachmentChanges = (c: OfflineAttachmentChanges) =>
  c.media.add.length + c.media.remove.length + c.documents.add.length + c.documents.remove.length > 0;

async function queueSave({ req, store, queue }: WriteContext) {
  const body = req.body;
  // A move is routed to BL-2448's handler before this.
  if (!isRecord(body) || 'moveToAuctionId' in body) return unqueueable();
  const { mediaChanges, documentChanges, ...fields } = body;
  const saved = attachmentChangesOf({ mediaChanges, documentChanges });
  if (!saved) return unqueueable();
  const { attachments, order } = saved;
  const lotId = req.params.id;
  const lot = lotId ? await store.records.get({ collection: 'lots', key: lotId }) : null;
  if (!lotId || !lot) return NOT_FOUND;
  const { changes, baseValues } = diffLotPatch({ body: fields, baseline: lotPatchBaseline(lot.detail) });
  const editsFields = Object.keys(changes).length > 0;
  if (!editsFields && !hasAttachmentChanges(attachments) && !order) return { kind: 'ok' as const, data: { id: lotId } };
  const edited = editsFields ? applyLotEditOverlay({ lot, changes, reference: await referenceFor(store, changes) }) : lot;
  const overlaid = applyLotAttachmentOverlay({ lot: edited, changes: attachments });
  // Past the per-lot cap: the server would refuse the add, so it is never queued.
  if (!overlaid.ok) return unqueueable();
  // BL-2418: the photo order folds onto the device copy after the adds and removes, as the server does.
  const next = order ? applyOfflineMediaOrder({ lot: overlaid.lot, order }) : overlaid.lot;
  const createId = await pendingCreateOf(store, lotId);
  const titleOf = next.detail.name;
  const numberOf = formatLotNumber(next.detail);
  // BL-2681: the Save folds into the lot's desired state, so several Saves send as one request. A
  // lot that is still a queued create takes the fields into the create itself. The state is read
  // inside the write's batch, so a request landing meanwhile is not overwritten by a stale copy.
  const stateLabel = (current: LotDesiredState | null): ChangeQueueLabel => ({
    lotNumber: numberOf,
    title: titleOf,
    newLot: createId !== null || current?.kind === 'create',
  });
  const lotStateChange = editsFields
    ? (current: LotDesiredState | null) =>
        applySave(current, {
          lotId,
          tenantId: store.scope.tenantId,
          kind: 'edit',
          fields: changes as Record<string, ChangeQueueJson>,
          baseValues: baseValues as Record<string, ChangeQueueJson>,
          label: stateLabel(current),
          now: Date.now(),
        })
    : undefined;
  const label = stateLabel(await store.lotStates.get(lotId));
  const entries = [
    ...attachmentQueueEntries({ lotId, label, dependsOn: createId, newId }, attachments),
    // After this Save's photo adds and removes, which the replay lands first (BL-2418). The base is the
    // order the device held before this Save, so a newer order from another device is refused.
    ...(order
      ? [mediaOrderQueueEntry({ id: newId(), lotId, label, dependsOn: createId, order, baseOrder: lot.detail.media.map((m) => m.id) })]
      : []),
  ];
  await commitLocalWrite({ store, queue, entries, lotStateChange, lot: { key: lotId, value: next, parentKey: next.detail.auctionId } });
  // BUG-2081: the lists show the Save's new photos at once, from list-sized thumbnails.
  await cacheStagedListThumbnails({ store, lotId, auctionId: next.detail.auctionId, adds: attachments.media.add });
  return { kind: 'ok' as const, data: { id: lotId } };
}

/**
 * Keep a new lot's create as its desired state (BL-2681), with one add per photo and document
 * picked offline (waiting on it), and file its device record: its fields, its staged files and its
 * create-sheet notes. The lot keeps the id made here, so nothing is rewritten when it syncs.
 */
async function queueCreate(input: {
  ctx: WriteContext;
  payload: LotCreateRequestPayload;
  currency: string;
  placement: OfflineNewLotPlacement | null;
}): Promise<string | null> {
  const { ctx: { store, queue }, payload, placement } = input;
  const clientId = newId();
  const at = new Date().toISOString();
  const split = splitOfflineCreateFiles({ payload, at });
  // A file already uploaded has no place in the state's fields, so it is refused here, never dropped on the way to the server.
  if (split.payload.stagedMedia != null || split.payload.stagedDocuments != null) return null;
  const base = buildOfflineNewLot({
    clientId,
    tenantId: store.scope.tenantId,
    currency: input.currency,
    payload: split.payload,
    placement,
    reference: await referenceFor(store, payload),
    at,
  });
  const author = payload.internalNotes?.length ? await sessionAuthor(store) : null;
  const withNotes = author ? { ...base, notes: createSheetNotes({ clientId, bodies: payload.internalNotes ?? [], author, at }) } : base;
  const overlaid = applyLotAttachmentOverlay({ lot: withNotes, changes: split.changes });
  if (!overlaid.ok) return unqueueable();
  const label: ChangeQueueLabel = { lotNumber: null, title: payload.name, newLot: true };
  const lotState = applySave(null, {
    lotId: clientId,
    tenantId: store.scope.tenantId,
    kind: 'create',
    auctionId: placement?.auctionId ?? null,
    fields: definedFieldsOf(split.payload),
    baseValues: {},
    label,
    now: Date.now(),
  });
  const entries = attachmentQueueEntries({ lotId: clientId, label, dependsOn: null, newId }, split.changes);
  await commitLocalWrite({ store, queue, entries, lotState, lot: { key: clientId, value: overlaid.lot, parentKey: placement?.auctionId ?? null } });
  await cacheStagedListThumbnails({ store, lotId: clientId, auctionId: placement?.auctionId ?? null, adds: split.changes.media.add });
  return clientId;
}

/**
 * A create payload's fields as the state holds them: JSON, the keys the form left undefined dropped, and only
 * the keys the state write's `fields` accepts (not `tenantId` or `clientOpId`), so settling a create leaves no
 * follow-up edit the server's closed key set would refuse.
 */
function definedFieldsOf(payload: LotCreateRequestPayload): Record<string, ChangeQueueJson> {
  return Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => value !== undefined && LOT_STATE_WRITE_FIELD_KEYS.includes(key)),
  ) as Record<string, ChangeQueueJson>;
}

async function queuePlace(ctx: WriteContext) {
  const body = ctx.req.body;
  if (!isRecord(body) || body.targetKind !== 'auction' || typeof body.targetId !== 'string') return unqueueable();
  const items = Array.isArray(body.items) ? body.items : [];
  // A relist (`sourceLotId`) is routed to BL-2448's handler before this; several lots at once is not queued.
  const payload = items.length === 1 && isRecord(items[0]) ? newLotOf(items[0].newLot) : null;
  if (!payload) return unqueueable();
  const auctionId = body.targetId;
  const auction = await ctx.store.records.get({ collection: 'auctions', key: auctionId });
  if (!auction) return NOT_FOUND;
  const clientId = await queueCreate({
    ctx,
    payload,
    currency: auction.auction.currency,
    placement: { auctionId, auctionName: auction.listRow.title ?? null, auctionTimeZone: null },
  });
  if (clientId == null) return unqueueable();
  return { kind: 'ok' as const, data: { placed: [{ id: clientId, lotId: clientId, verb: 'created' as const }], lots: [] } };
}

async function queueInventoryCreate(ctx: WriteContext) {
  const { req, store } = ctx;
  const body = req.body;
  const payload = newLotOf(body);
  if (!isRecord(body) || !payload || body.tenantId !== store.scope.tenantId) return unqueueable();
  const session = await store.records.get({ collection: 'session', key: OFFLINE_SESSION_RECORD_KEY });
  const houseCurrency = session?.tenants.userTenants.find((t) => t.id === store.scope.tenantId)?.currency ?? null;
  const currency = payload.startPrice?.currency ?? payload.reserve?.currency ?? houseCurrency;
  // A lot whose prices have no currency to show in can't be put on the device's lists.
  if (!currency) return unqueueable();
  const clientId = await queueCreate({
    ctx,
    payload,
    currency,
    placement: null,
  });
  if (clientId == null) return unqueueable();
  return { kind: 'ok' as const, data: { inventoryItemId: clientId, inventoryNumber: '', lotId: clientId } };
}

/** The lot writes, keyed `"<registry key>:<VERB>"`; typed by `OfflineHandlers` where composed. */
export const lotWriteHandlers = {
  'lots.id.index:PATCH': (req: OfflineRequest) => withQueue(req, queueSave),
  'lots.placeInAuction:POST': (req: OfflineRequest) => withQueue(req, queuePlace),
  'inventory.index:POST': (req: OfflineRequest) => withQueue(req, queueInventoryCreate),
};
